import React, { useEffect, useCallback, useRef, useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useStore } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
// Note: LexicalSelectionChangePlugin might not be available in this version
import { HeadingNode, QuoteNode, $createHeadingNode } from '@lexical/rich-text';
import { ListNode, ListItemNode, $createListNode, $createListItemNode } from '@lexical/list';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createParagraphNode,
  $createTextNode,
  $createTabNode,
  $createLineBreakNode,
  $getRoot,
} from 'lexical';
import axiosWrapper from '../../../../../../../../../utils/apiRequests/axiosWrapper';
import { cacheUpdate } from '../../../../../../../../../utils/cache/cacheHelpers';
import useThemeStyles from '../../../../../../../../../hooks/useThemeStyles';
import { useEditorContext } from '../../../../../../../../../contexts/EditorContext';
import { useProjectAccess } from '../../../../../../../../../contexts/ProjectAccessContext';
import {
  setEditorState,
  consumeSceneEditorUnmountPersistSkip,
} from '../../../../../../../../../features/editor/editorSlice';
import StyledTextPlugin from '../../../../../../../../Editor/Components/Plugins/StyledTextPlugin';
import {
  StyledTextNode,
  $createStyledTextNode,
} from '../../../../../../../../Editor/Components/Plugins/StyledTextNode';
import IndentPlugin from '../../../../../../../../Editor/Components/Plugins/Indentation/IndentPlugin';
import TabPlugin from '../../../../../../../../Editor/Components/Plugins/TabPlugin';
import SentimentPlugin from '../../../../../../../../Editor/Components/Plugins/Sentiment/SentimentPlugin';

/** Tag for `editor.update` when loading from props; update listener ignores it for hasLocalEditsRef. */
const LEXICAL_SCENE_HYDRATION_TAG = 'plotwyze-hydration';

// Helper function to create text content from node children
const createTextContent = (
  children,
  parentNode,
  defaultTextColor = '#000000',
  defaultHighlightColor = '#FFFF00'
) => {
  if (!children) return;

  children.forEach((child) => {
    if (child.type === 'text') {
      const textNode = $createTextNode(child.text || '');

      // Apply format if available
      if (child.format !== undefined) {
        textNode.setFormat(child.format);
      }

      // Apply style if available
      if (child.style) {
        textNode.setStyle(child.style);
      }

      parentNode.append(textNode);
    } else if (child.type === 'styled-text') {
      // Handle new StyledTextNode
      const styledNode = $createStyledTextNode(child.text || '', child.style || {});

      if (child.format !== undefined) {
        styledNode.setFormat(child.format);
      }

      if (child.detail !== undefined) {
        styledNode.setDetail(child.detail);
      }

      if (child.mode !== undefined) {
        styledNode.setMode(child.mode);
      }

      parentNode.append(styledNode);
    } else if (child.type === 'color') {
      // Legacy: Convert ColorNode to StyledTextNode
      let { text: textContent = '', format } = child;

      if (child.children && child.children.length > 0) {
        const [firstChild] = child.children;
        const { text, format: firstChildFormat } = firstChild;
        textContent = text || '';
        format = firstChildFormat !== undefined ? firstChildFormat : format;
      }

      const styledNode = $createStyledTextNode(textContent, {
        color: child.color || defaultTextColor,
      });

      if (format !== undefined) {
        styledNode.setFormat(format);
      }

      parentNode.append(styledNode);
    } else if (child.type === 'fontSize') {
      // Legacy: Convert FontSizeNode to StyledTextNode
      let { text: textContent = '', format } = child;

      if (child.children && child.children.length > 0) {
        const [firstChild] = child.children;
        const { text, format: firstChildFormat } = firstChild;
        textContent = text || '';
        format = firstChildFormat !== undefined ? firstChildFormat : format;
      }

      const styledNode = $createStyledTextNode(textContent, {
        fontSize: child.fontSize || '16px',
      });

      if (format !== undefined) {
        styledNode.setFormat(format);
      }

      parentNode.append(styledNode);
    } else if (child.type === 'fontFamily') {
      // Legacy: Convert FontFamilyNode to StyledTextNode
      let { text: textContent = '', format } = child;

      if (child.children && child.children.length > 0) {
        const [firstChild] = child.children;
        const { text, format: firstChildFormat } = firstChild;
        textContent = text || '';
        format = firstChildFormat !== undefined ? firstChildFormat : format;
      }

      const styledNode = $createStyledTextNode(textContent, {
        fontFamily: child.fontFamily || 'Arial',
      });

      if (format !== undefined) {
        styledNode.setFormat(format);
      }

      parentNode.append(styledNode);
    } else if (child.type === 'highlight') {
      // Legacy: Convert HighlightNode to StyledTextNode
      let { text: textContent = '', format } = child;

      if (child.children && child.children.length > 0) {
        const [firstChild] = child.children;
        const { text, format: firstChildFormat } = firstChild;
        textContent = text || '';
        format = firstChildFormat !== undefined ? firstChildFormat : format;
      }

      const styledNode = $createStyledTextNode(textContent, {
        backgroundColor: child.color || defaultHighlightColor,
      });

      if (format !== undefined) {
        styledNode.setFormat(format);
      }

      parentNode.append(styledNode);
    } else if (child.type === 'tab') {
      // Imported documents use tab nodes for indentation; dropping them loses content.
      parentNode.append($createTabNode());
    } else if (child.type === 'linebreak') {
      parentNode.append($createLineBreakNode());
    }
  });
};

/** Recursively build a Lexical list node from persisted JSON (supports nested lists). */
const buildListNode = (nodeData) => {
  let listType = nodeData.listType || 'bullet';
  if (nodeData.tag === 'ol') {
    listType = 'number';
  } else if (nodeData.tag === 'ul') {
    listType = 'bullet';
  }

  const listNode = $createListNode(listType);
  if (nodeData.start !== undefined) {
    listNode.setStart(nodeData.start);
  }

  (nodeData.children || []).forEach((listItemData) => {
    if (listItemData.type !== 'listitem') return;

    const listItemNode = $createListItemNode();
    if (listItemData.value !== undefined) {
      listItemNode.setValue(listItemData.value);
    }
    if (listItemData.checked !== undefined) {
      listItemNode.setChecked(listItemData.checked);
    }

    const itemChildren = listItemData.children || [];
    createTextContent(
      itemChildren.filter((c) => c.type !== 'list'),
      listItemNode
    );
    listNode.append(listItemNode);

    // Nested lists live inside their own listitem wrapper (Lexical convention)
    itemChildren
      .filter((c) => c.type === 'list')
      .forEach((nested) => {
        const nestedItem = $createListItemNode();
        nestedItem.append(buildListNode(nested));
        listNode.append(nestedItem);
      });
  });

  return listNode;
};

/** Build editor tree from persisted Lexical JSON (same shape as scene.data). */
const applySceneDataToRoot = (sceneData) => {
  if (!sceneData?.root?.children) {
    const root = $getRoot();
    root.clear();
    root.append($createParagraphNode());
    return;
  }

  const root = $getRoot();
  root.clear();

  sceneData.root.children.forEach((nodeData) => {
    if (nodeData.type === 'list') {
      root.append(buildListNode(nodeData));
    } else if (nodeData.type === 'paragraph' || nodeData.type === 'heading') {
      const element =
        nodeData.type === 'heading'
          ? $createHeadingNode(/^h[1-6]$/.test(nodeData.tag) ? nodeData.tag : 'h2')
          : $createParagraphNode();

      if (nodeData.indent !== undefined && nodeData.indent > 0) {
        element.setIndent(nodeData.indent);
      }

      // Element-level alignment ('center' | 'right' | 'justify' | ...)
      if (typeof nodeData.format === 'string' && nodeData.format) {
        element.setFormat(nodeData.format);
      }

      createTextContent(nodeData.children, element);

      root.append(element);
    }
  });

  if (root.getChildrenSize() === 0) {
    root.append($createParagraphNode());
  }
};

function lexicalPlainTextFromJson(json) {
  if (!json?.root?.children) return '';
  let out = '';
  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return;
    nodes.forEach((n) => {
      if (n == null) return;
      if (typeof n.text === 'string') out += n.text;
      if (n.children) walk(n.children);
    });
  };
  walk(json.root.children);
  return out;
}

function isParagraphLikeNodeEmpty(node) {
  if (!node || typeof node !== 'object') return true;
  if (!node.children || node.children.length === 0) return true;
  return node.children.every((c) => {
    if (c.type === 'text' || c.type === 'styled-text') {
      return !(c.text && String(c.text).trim());
    }
    return isParagraphLikeNodeEmpty(c);
  });
}

/** No meaningful text and only empty blocks (or none). Handles Lexical vs server JSON shape drift. */
function isEffectivelyEmptyLexicalDocJson(json) {
  if (!json?.root) return true;
  if (lexicalPlainTextFromJson(json).trim() !== '') return false;
  const children = json.root.children ?? [];
  if (children.length === 0) return true;
  return children.every((n) => {
    if (n.type === 'paragraph' || (n.type === 'heading' && n.tag)) {
      return isParagraphLikeNodeEmpty(n);
    }
    if (n.type === 'quote') {
      return isParagraphLikeNodeEmpty(n);
    }
    if (n.type === 'list') {
      const items = n.children ?? [];
      return items.every((li) => li?.type === 'listitem' && isParagraphLikeNodeEmpty(li));
    }
    return false;
  });
}

/**
 * Avoid unmount PUT when nothing meaningful changed vs the scene snapshot. Lexical often sets
 * hasLocalEdits after hydration-only updates, which caused DELETE scene → unmount PUT → 404.
 */
function shouldSkipUnmountPersist(currentStateJson, latestScene) {
  const baselineDoc = latestScene?.data ?? (latestScene?.root ? { root: latestScene.root } : null);
  const bRoot = baselineDoc?.root;

  if (bRoot && JSON.stringify(currentStateJson.root) === JSON.stringify(bRoot)) {
    return true;
  }

  const curEmpty = isEffectivelyEmptyLexicalDocJson(currentStateJson);
  const baseEmpty = !bRoot || isEffectivelyEmptyLexicalDocJson({ root: bRoot });
  return curEmpty && baseEmpty;
}

// Plugin to load scene content when scene.data arrives or changes (e.g. after refresh).
const InitialStatePlugin = ({
  sceneData,
  hasLocalEditsRef,
  suppressHydrationRef,
  hydrationNonce = 0,
}) => {
  const [editor] = useLexicalComposerContext();
  const { canEdit: hydrationCanEdit } = useProjectAccess();
  const loadedSignatureRef = useRef(undefined);
  const lastHydrationNonceRef = useRef(undefined);
  const hydrationSuppressRef = suppressHydrationRef;

  useEffect(() => {
    const signature = sceneData?.root ? JSON.stringify(sceneData.root) : '';

    let forcedHydration = false;
    if (lastHydrationNonceRef.current === undefined) {
      lastHydrationNonceRef.current = hydrationNonce;
    } else if (hydrationNonce !== lastHydrationNonceRef.current) {
      forcedHydration = true;
      lastHydrationNonceRef.current = hydrationNonce;
      // eslint-disable-next-line no-param-reassign -- mutable ref slot from parent
      if (hasLocalEditsRef) hasLocalEditsRef.current = false;
      loadedSignatureRef.current = undefined;
    }

    if (!forcedHydration && signature === loadedSignatureRef.current) {
      return;
    }

    // After the first load, do not replace editor content from props if the user has already typed
    // (avoids a late API response overwriting in-progress work before debounced save runs).
    if (
      !forcedHydration &&
      hasLocalEditsRef?.current &&
      loadedSignatureRef.current !== undefined &&
      signature !== loadedSignatureRef.current
    ) {
      return;
    }

    loadedSignatureRef.current = signature;

    editor.setEditable(hydrationCanEdit);

    if (hydrationSuppressRef) {
      hydrationSuppressRef.current = true;
    }

    editor.update(
      () => {
        applySceneDataToRoot(sceneData);
      },
      { tag: LEXICAL_SCENE_HYDRATION_TAG }
    );

    queueMicrotask(() => {
      if (hydrationSuppressRef) {
        hydrationSuppressRef.current = false;
      }
    });

    setTimeout(() => {
      const rootElement = editor.getRootElement();
      if (!rootElement) return;
      const paragraphs = rootElement.querySelectorAll('p');
      paragraphs.forEach((paragraphElement) => {
        const lexicalKey = Array.from(paragraphElement.attributes).find((attr) =>
          attr.name.startsWith('data-lexical-')
        )?.value;

        if (lexicalKey) {
          const node = editor.getEditorState()._nodeMap.get(lexicalKey);
          if (node && node.__type === 'paragraph' && node.__indent > 0) {
            // eslint-disable-next-line no-param-reassign
            paragraphElement.style.paddingLeft = `${node.__indent * 40}px`;
            paragraphElement.setAttribute('data-indent', node.__indent.toString());
          }
        }
      });
    }, 50);
  }, [editor, sceneData, hasLocalEditsRef, hydrationSuppressRef, hydrationNonce, hydrationCanEdit]);

  return null;
};

// Simple plugin to handle text input formatting
// This plugin is disabled for now as it was interfering with Enter key functionality
// TODO: Re-implement using Lexical's command system instead of DOM events
const TextInputFormattingPlugin = () => {
  // const { formattingState } = useEditorContext();
  // const [editor] = useLexicalComposerContext();

  // Disabled to prevent interference with Enter key
  return null;
};

// Lexical Editor Content Component
const EditorContent = ({
  scene,
  sceneLexicalData,
  onChange,
  onFlushSave,
  hasLocalEditsRef,
  suppressHydrationRef: hydrationSuppressPropRef,
  editorId,
  onEditorReady,
  hydrationNonce,
}) => {
  const hydrationSuppressRef = hydrationSuppressPropRef;
  const [editor] = useLexicalComposerContext();
  const { registerEditor, unregisterEditor, syncFormattingWithEditor } = useEditorContext();
  const { colors, isDark } = useThemeStyles();

  // Register this editor with the context (only on focus)
  const handleRegisterEditor = useCallback(() => {
    registerEditor(editor, editorId);
  }, [registerEditor, editor, editorId]);

  const handleUnregisterEditor = useCallback(() => {
    unregisterEditor(editorId);
  }, [unregisterEditor, editorId]);

  // Notify parent component when editor is ready
  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  // Don't register editor immediately - only register on focus
  useEffect(() => {
    return () => {
      handleUnregisterEditor();
    };
  }, [handleUnregisterEditor]);

  // Load scene content from children array (fallback for older scenes)
  const loadSceneContent = useCallback(() => {
    if (scene?.children && scene.children.length > 0 && !sceneLexicalData) {
      if (hydrationSuppressRef) {
        hydrationSuppressRef.current = true;
      }
      editor.update(
        () => {
          const root = $getRoot();
          root.clear();
          scene.children.forEach((child) => {
            const paragraph = $createParagraphNode();
            paragraph.append($createTextNode(child.text || ''));
            root.append(paragraph);
          });
        },
        { tag: LEXICAL_SCENE_HYDRATION_TAG }
      );
      queueMicrotask(() => {
        if (hydrationSuppressRef) {
          hydrationSuppressRef.current = false;
        }
      });
    }
  }, [editor, scene?.children, sceneLexicalData, hydrationSuppressRef]);

  useEffect(() => {
    loadSceneContent();
  }, [loadSceneContent]);

  // Handle focus to make this editor active
  const handleFocus = useCallback(() => {
    // Prevent browser from scrolling the chapter into view when focusing on a scene editor
    const scrollContainer = document.getElementById('manuscript-scroll-container');

    // Store current scroll position before focus causes any scrolling
    let scrollTop;
    let scrollLeft;

    if (scrollContainer) {
      scrollTop = scrollContainer.scrollTop;
      scrollLeft = scrollContainer.scrollLeft;
    } else {
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    }

    // Register editor
    handleRegisterEditor();

    // Restore scroll position multiple times to ensure it sticks
    // The browser may try to scroll multiple times, so we need to counteract it
    const restoreScroll = () => {
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollTop;
        scrollContainer.scrollLeft = scrollLeft;
      } else {
        window.scrollTo(scrollLeft, scrollTop);
      }
    };

    // Restore immediately and after a few frames to catch any delayed scroll behavior
    restoreScroll();
    requestAnimationFrame(restoreScroll);
    requestAnimationFrame(() => {
      requestAnimationFrame(restoreScroll);
    });
  }, [handleRegisterEditor]);

  // Handle selection changes to sync formatting state
  const handleSelectionChange = useCallback(() => {
    if (editor) {
      syncFormattingWithEditor(editor);
    }
  }, [editor, syncFormattingWithEditor]);

  // Mark "local edits" for any Lexical change that is not a prop-driven scene load. Relying only on
  // OnChange + scheduleSave missed cases: onChange can be skipped when suppressHydrationRef is set
  // during the same update that applied a highlight, so hasLocalEditsRef never flips and a later
  // stale `scene` prop can re-hydrate and strip highlights.
  useEffect(() => {
    return editor.registerUpdateListener(({ tags }) => {
      if (hydrationSuppressRef?.current) return;
      if (tags && typeof tags.has === 'function' && tags.has(LEXICAL_SCENE_HYDRATION_TAG)) return;
      // eslint-disable-next-line no-param-reassign -- mutable ref from parent EditorBlock
      hasLocalEditsRef.current = true;
    });
  }, [editor, hydrationSuppressRef, hasLocalEditsRef]);

  return (
    <>
      <InitialStatePlugin
        sceneData={sceneLexicalData}
        hasLocalEditsRef={hasLocalEditsRef}
        suppressHydrationRef={hydrationSuppressRef}
        hydrationNonce={hydrationNonce}
      />
      <RichTextPlugin
        contentEditable={
          <ContentEditable
            style={{
              minHeight: '120px',
              padding: '12px',
              border: 'none',
              outline: 'none',
              fontSize: '12px',
              lineHeight: '1.6',
              fontFamily: 'inherit',
              cursor: 'text',
              color: isDark ? '#e2e8f0' : colors.text.primary,
            }}
            className="editor-text-formatting"
            onFocus={handleFocus}
            onBlur={(e) => {
              const next = e.relatedTarget;
              // Clicking the sticky toolbar (focus moves away from contenteditable); don't treat as
              // leaving the manuscript — avoids flush/save churn fighting Lexical formatting.
              if (
                next &&
                typeof next.closest === 'function' &&
                next.closest('#manuscript-action-bar')
              ) {
                return;
              }
              onFlushSave?.('blur');
            }}
          />
        }
        placeholder={
          <div
            style={{
              position: 'absolute',
              color: '#999',
              pointerEvents: 'none',
              padding: '12px',
              fontSize: '12px',
            }}
          >
            Start editing this scene...
          </div>
        }
        ErrorBoundary={LexicalErrorBoundary}
      />
      <OnChangePlugin
        onChange={(editorState) => {
          onChange(editorState);
          handleSelectionChange();
        }}
      />
      <HistoryPlugin />
      <ListPlugin />
      <CheckListPlugin />
      <TabPlugin />
      <StyledTextPlugin />
      <IndentPlugin />
      <TextInputFormattingPlugin />
      <SentimentPlugin />
    </>
  );
};

/* eslint-disable react/require-default-props */
EditorContent.propTypes = {
  scene: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    root: PropTypes.shape({
      children: PropTypes.arrayOf(PropTypes.object),
    }),
    children: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
        type: PropTypes.string,
      })
    ),
  }),
  sceneLexicalData: PropTypes.shape({
    root: PropTypes.shape({
      children: PropTypes.arrayOf(PropTypes.object),
    }),
  }),
  onChange: PropTypes.func.isRequired,
  onFlushSave: PropTypes.func,
  hasLocalEditsRef: PropTypes.shape({ current: PropTypes.bool }),
  suppressHydrationRef: PropTypes.shape({ current: PropTypes.bool }),
  editorId: PropTypes.string.isRequired,
  onEditorReady: PropTypes.func,
  hydrationNonce: PropTypes.number,
};
/* eslint-enable react/require-default-props */

function getLexicalRootFromOverlay(overlayEl) {
  if (!overlayEl) return null;
  // Lexical's ContentEditable sets `contentEditable` from `editor.isEditable()`. During split
  // placement we call `setEditable(false)`, so the root is `contenteditable="false"`.
  // `requestAnimationFrame` in the zone measurer often runs after that effect, so only
  // matching `"true"` yields no root and no gap highlights.
  return (
    overlayEl.querySelector('[contenteditable="true"]') ??
    overlayEl.querySelector('[contenteditable="false"]') ??
    overlayEl.querySelector('[contenteditable=""]')
  );
}

function computeSplitGapZones(overlayEl) {
  if (!overlayEl) return [];
  const editableRoot = getLexicalRootFromOverlay(overlayEl);
  if (!editableRoot) return [];

  let blockList = Array.from(editableRoot.children).filter(
    (el) => el.matches && el.matches('p, h1, h2, h3, h4, h5, h6, ul, ol, blockquote')
  );
  if (blockList.length === 0) {
    blockList = Array.from(editableRoot.querySelectorAll('p, h1, h2, h3, ul, ol')).filter((el) => {
      let p = el.parentElement;
      while (p && p !== editableRoot) {
        if (p.matches?.('li')) return false;
        p = p.parentElement;
      }
      return true;
    });
  }

  const parentRect = overlayEl.getBoundingClientRect();
  const n = blockList.length;
  if (n === 0) return [];

  const zones = [];
  for (let splitIndex = 0; splitIndex < n; splitIndex += 1) {
    let gapTop;
    let gapBottom;
    if (splitIndex === 0) {
      const br = blockList[0].getBoundingClientRect();
      gapTop = parentRect.top;
      gapBottom = br.top;
    } else {
      gapTop = blockList[splitIndex - 1].getBoundingClientRect().bottom;
      gapBottom = blockList[splitIndex].getBoundingClientRect().top;
    }
    const gapMid = (gapTop + gapBottom) / 2;
    const naturalGap = Math.max(gapBottom - gapTop, 0);
    const stripHeight = Math.min(32, Math.max(14, naturalGap > 0 ? naturalGap + 8 : 16));
    const stripTopViewport = gapMid - stripHeight / 2;
    const top = stripTopViewport - parentRect.top + overlayEl.scrollTop;
    zones.push({ splitIndex, top, height: stripHeight });
  }
  return zones;
}

const EditorBlock = ({
  scene,
  editorIndex,
  onDragOver,
  onDrop,
  isDragging,
  onSplitScene,
  hydrationNonce = 0,
  splitPlacementActive = false,
  onCancelSplitPlacement,
  onSceneWordCountUpdate = undefined,
  onChapterWordCountUpdate = undefined,
}) => {
  const editorContentRef = useRef(null);
  const splitOverlayContainerRef = useRef(null);
  const dispatch = useDispatch();
  const store = useStore();
  const storeRef = useRef(store);
  storeRef.current = store;
  const { colors, isDark } = useThemeStyles();
  const { canEdit } = useProjectAccess();
  const canEditRef = useRef(canEdit);
  canEditRef.current = canEdit;
  const debounceTimeoutRef = useRef(null);
  const editorRef = useRef(null);
  const hasLocalEditsRef = useRef(false);
  const suppressHydrationRef = useRef(false);
  const sceneRef = useRef(scene);
  sceneRef.current = scene;

  const [splitGapZones, setSplitGapZones] = useState([]);
  const [editorEpoch, setEditorEpoch] = useState(0);

  const sceneLexicalData = scene?.data ?? (scene?.root ? { root: scene.root } : null);

  // Create unique editor ID
  const editorId = `SceneEditor-${scene?.id || editorIndex}`;

  // Lexical editor configuration
  const initialConfig = {
    namespace: editorId,
    theme: {
      paragraph: 'editor-paragraph',
      heading: {
        h1: 'editor-h1',
        h2: 'editor-h2',
        h3: 'editor-h3',
      },
      text: {
        bold: 'editor-text-bold',
        italic: 'editor-text-italic',
        underline: 'editor-text-underline',
        strikethrough: 'editor-text-strikethrough',
      },
      indent: 'editor-indent',
    },
    onError: (error) => {
      console.error('Lexical Editor Error:', error);
    },
    nodes: [HeadingNode, ListNode, ListItemNode, QuoteNode, AutoLinkNode, LinkNode, StyledTextNode],
    // Viewers/commenters on shared projects get a read-only editor.
    editable: canEdit,
    // Ensure editor starts with an empty paragraph for proper Enter key functionality
    editorState: null,
  };

  const persistEditorState = useCallback(
    async (reason = 'save') => {
      const latestScene = sceneRef.current;

      // Read-only roles never save (the server would reject it anyway).
      if (!canEditRef.current) return;

      if (!editorRef.current || !latestScene?.id) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('[PlotWyze SceneEditor] Save skipped:', {
            reason,
            hasEditor: !!editorRef.current,
            sceneId: latestScene?.id,
          });
        }
        return;
      }

      const editorState = editorRef.current.getEditorState();
      const stateJSON = editorState.toJSON();

      if (
        (reason === 'unmount' || reason === 'tab-hidden') &&
        shouldSkipUnmountPersist(stateJSON, latestScene)
      ) {
        return;
      }

      if (reason === 'unmount' || reason === 'tab-hidden') {
        const skipMap = storeRef.current.getState().editor.sceneEditorUnmountPersistSkipIds;
        if (skipMap && latestScene?.id) {
          const keys = [String(latestScene.id)];
          const mongoId = latestScene._id != null ? String(latestScene._id) : '';
          if (mongoId && mongoId !== String(latestScene.id ?? '')) {
            keys.push(mongoId);
          }
          if (keys.some((k) => skipMap[k])) {
            keys.forEach((k) => {
              if (skipMap[k]) dispatch(consumeSceneEditorUnmountPersistSkip(k));
            });
            return;
          }
        }
      }

      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.info(
          `[PlotWyze SceneEditor] PUT editor-state (${reason}) → scene ${latestScene.id}`
        );
      }

      // Pre-PUT Redux update resurrects deleted scenes if unmount runs after deleteScene/removeEditorState.
      const optimisticRedux = reason !== 'unmount';
      if (optimisticRedux) {
        dispatch(
          setEditorState({ id: latestScene.id, ...stateJSON, chapterId: latestScene?.chapterId })
        );
      }

      const { data, error } = await axiosWrapper({
        method: 'put',
        path: `scenes/${latestScene?.id}/editor-state`,
        data: { editorState: stateJSON },
      });

      if (error?.response?.status === 404) {
        return;
      }

      if (error) {
        console.error('Failed to save editor state:', error);
        return;
      }

      if (!optimisticRedux) {
        dispatch(
          setEditorState({ id: latestScene.id, ...stateJSON, chapterId: latestScene?.chapterId })
        );
      }

      if (process.env.NODE_ENV === 'development' && !error) {
        // eslint-disable-next-line no-console
        console.info(`[PlotWyze SceneEditor] Saved OK (${reason}) scene ${latestScene.id}`);
      }

      // getScenes() reads IndexedDB before the network; keep cache in sync after PUT.
      if (!error && latestScene._id) {
        try {
          await cacheUpdate('scenes', {
            ...latestScene,
            data: stateJSON,
            wordCount:
              typeof data?.scene?.wordCount === 'number'
                ? data.scene.wordCount
                : latestScene.wordCount,
            updatedAt: new Date().toISOString(),
          });
        } catch (cacheErr) {
          console.warn('[PlotWyze SceneEditor] IndexedDB scene cache update failed:', cacheErr);
        }
      }

      if (data) {
        const sceneIdentifier = latestScene?.id || latestScene?._id;
        const sceneWordCount = data.scene?.wordCount;
        const chapterWordCount = data.chapter?.wordCount;

        if (
          typeof sceneWordCount === 'number' &&
          sceneIdentifier &&
          typeof onSceneWordCountUpdate === 'function'
        ) {
          onSceneWordCountUpdate(sceneIdentifier, sceneWordCount);
        }

        if (
          typeof chapterWordCount === 'number' &&
          typeof onChapterWordCountUpdate === 'function'
        ) {
          onChapterWordCountUpdate(chapterWordCount);
        }
      }
    },
    [dispatch, onSceneWordCountUpdate, onChapterWordCountUpdate]
  );

  const persistEditorStateRef = useRef(persistEditorState);
  persistEditorStateRef.current = persistEditorState;

  const flushPendingSave = useCallback((reason = 'flush') => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    persistEditorStateRef.current(reason);
  }, []);

  const scheduleSave = useCallback(() => {
    if (suppressHydrationRef.current) {
      return;
    }
    hasLocalEditsRef.current = true;
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      debounceTimeoutRef.current = null;
      persistEditorStateRef.current('debounced');
    }, 1200);
  }, []);

  const handleEditorReady = useCallback((editor) => {
    editorRef.current = editor;
    setEditorEpoch((n) => n + 1);
  }, []);

  const cancelDebouncedSave = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  }, []);

  const runSplitAtLexicalIndex = useCallback(
    (splitAtIndex) => {
      if (!onSplitScene || !editorRef.current) return;
      cancelDebouncedSave();
      const fullJson = editorRef.current.getEditorState().toJSON();
      const { root } = fullJson;
      const children = root?.children || [];
      if (splitAtIndex >= children.length || splitAtIndex < 0) return;
      const moving = children.slice(splitAtIndex);
      if (moving.length === 0) return;

      const remainingStateJson = {
        ...fullJson,
        root: {
          ...root,
          children: children.slice(0, splitAtIndex),
        },
      };
      const movingStateJson = {
        ...fullJson,
        root: {
          ...root,
          children: moving,
        },
      };

      onSplitScene(editorIndex, { remainingStateJson, movingStateJson });
    },
    [cancelDebouncedSave, editorIndex, onSplitScene]
  );

  useLayoutEffect(() => {
    if (!splitPlacementActive || !splitOverlayContainerRef.current) {
      setSplitGapZones([]);
      return undefined;
    }

    let raf = 0;
    const refreshZones = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!splitOverlayContainerRef.current) return;
        setSplitGapZones(computeSplitGapZones(splitOverlayContainerRef.current));
      });
    };

    refreshZones();

    const el = splitOverlayContainerRef.current;
    const ro = new ResizeObserver(refreshZones);
    ro.observe(el);

    window.addEventListener('resize', refreshZones);
    document.addEventListener('scroll', refreshZones, true);

    const editor = editorRef.current;
    const unsub =
      editor?.registerUpdateListener?.(() => {
        refreshZones();
      }) ?? undefined;

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', refreshZones);
      document.removeEventListener('scroll', refreshZones, true);
      if (typeof unsub === 'function') unsub();
    };
  }, [splitPlacementActive, hydrationNonce, scene?.id, editorEpoch]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return undefined;
    editor.setEditable(!splitPlacementActive);
    return () => {
      editor.setEditable(true);
    };
  }, [splitPlacementActive]);

  useEffect(() => {
    if (!splitPlacementActive) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onCancelSplitPlacement?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [splitPlacementActive, onCancelSplitPlacement]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
          debounceTimeoutRef.current = null;
        }
        persistEditorStateRef.current('tab-hidden');
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      persistEditorStateRef.current('unmount');
    };
    // Empty deps: only register listeners once per editor mount. Using persistEditorStateRef avoids
    // re-running cleanup when scene/cache updates replace the scene object (that caused an infinite loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let editorChromeCursor = 'text';
  if (splitPlacementActive) {
    editorChromeCursor = 'default';
  } else if (isDragging) {
    editorChromeCursor = 'copy';
  }

  return (
    <>
      <style>
        {`
          .editor-paragraph, .editor-h1, .editor-h2, .editor-h3 {
            color: ${isDark ? '#e2e8f0' : colors.text.primary} !important;
          }
          .editor-text-bold {
            font-weight: bold !important;
          }
          .editor-text-italic {
            font-style: italic !important;
          }
          .editor-text-underline {
            text-decoration: underline !important;
          }
          .editor-text-strikethrough {
            text-decoration: line-through !important;
          }
          /* List styles */
          ul, ol {
            padding-left: 24px;
            margin: 8px 0;
          }
          li {
            margin: 4px 0;
            color: ${isDark ? '#e2e8f0' : colors.text.primary} !important;
          }
          /* Support for combinations */
          .editor-text-bold.editor-text-italic {
            font-weight: bold !important;
            font-style: italic !important;
          }
          .editor-text-bold.editor-text-underline {
            font-weight: bold !important;
            text-decoration: underline !important;
          }
          .editor-text-bold.editor-text-strikethrough {
            font-weight: bold !important;
            text-decoration: line-through !important;
          }
          .editor-text-italic.editor-text-underline {
            font-style: italic !important;
            text-decoration: underline !important;
          }
          .editor-text-italic.editor-text-strikethrough {
            font-style: italic !important;
            text-decoration: line-through !important;
          }
          .editor-text-underline.editor-text-strikethrough {
            text-decoration: underline line-through !important;
          }
          /* Triple combinations */
          .editor-text-bold.editor-text-italic.editor-text-underline {
            font-weight: bold !important;
            font-style: italic !important;
            text-decoration: underline !important;
          }
          .editor-text-bold.editor-text-italic.editor-text-strikethrough {
            font-weight: bold !important;
            font-style: italic !important;
            text-decoration: line-through !important;
          }
          .editor-text-bold.editor-text-underline.editor-text-strikethrough {
            font-weight: bold !important;
            text-decoration: underline line-through !important;
          }
          .editor-text-italic.editor-text-underline.editor-text-strikethrough {
            font-style: italic !important;
            text-decoration: underline line-through !important;
          }
          /* All four combinations */
          .editor-text-bold.editor-text-italic.editor-text-underline.editor-text-strikethrough {
            font-weight: bold !important;
            font-style: italic !important;
            text-decoration: underline line-through !important;
          }
        `}
      </style>
      <Box
        ref={editorContentRef}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onDragOver={(e) => {
          if (splitPlacementActive || !isDragging || !onDragOver) return;

          e.preventDefault();
          e.stopPropagation();

          // Find which paragraph/node we're hovering over
          const editorElement = editorContentRef.current;
          if (!editorElement) return;

          // Get all paragraph elements in the editor
          const paragraphs = editorElement.querySelectorAll('p, h1, h2, h3, ul, ol');
          let nodeIndex = 0;
          let closestDistance = Infinity;

          paragraphs.forEach((paragraph, index) => {
            const rect = paragraph.getBoundingClientRect();
            const distance = Math.abs(e.clientY - rect.top);

            if (distance < closestDistance) {
              closestDistance = distance;
              // If mouse is below middle of paragraph, insert after it
              const middleY = rect.top + rect.height / 2;
              nodeIndex = e.clientY > middleY ? index + 1 : index;
            }
          });

          console.log('Drag over editor node:', {
            editorIndex,
            nodeIndex,
            paragraphCount: paragraphs.length,
          });
          onDragOver(e, editorIndex, nodeIndex);
        }}
        onDrop={(e) => {
          if (splitPlacementActive || !onDrop) return;

          e.preventDefault();
          e.stopPropagation();

          // Calculate final node index at drop
          const editorElement = editorContentRef.current;
          if (!editorElement) return;

          const paragraphs = editorElement.querySelectorAll('p, h1, h2, h3, ul, ol');
          let nodeIndex = 0;
          let closestDistance = Infinity;

          paragraphs.forEach((paragraph, index) => {
            const rect = paragraph.getBoundingClientRect();
            const distance = Math.abs(e.clientY - rect.top);

            if (distance < closestDistance) {
              closestDistance = distance;
              const middleY = rect.top + rect.height / 2;
              nodeIndex = e.clientY > middleY ? index + 1 : index;
            }
          });

          console.log('Dropped on editor at node:', { editorIndex, nodeIndex });
          onDrop(e, editorIndex, nodeIndex);
        }}
        sx={{
          border: `1px solid ${colors.border.subtle}`,
          borderRadius: 1,
          backgroundColor: isDark ? '#1e293b' : colors.paper.background,
          backgroundImage: isDark ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' : 'none',
          overflow: 'hidden',
          transition: 'all 0.2s ease-in-out',
          cursor: editorChromeCursor,
          '&:hover': {
            borderColor: colors.interactive.hover,
          },
          position: 'relative',
        }}
      >
        {splitPlacementActive && (
          <Box
            role="status"
            aria-live="polite"
            tabIndex={-1}
            onMouseDown={(e) => {
              // MUI Alert (previous) could steal focus on mount and break Lexical + toolbar formatting.
              // Keep focus in the editor unless the user explicitly presses Cancel.
              if (e.target.closest('button')) return;
              e.preventDefault();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
              py: 0.75,
              px: 1.25,
              mb: 0.75,
              borderRadius: 1,
              border: `1px solid ${alpha(colors.semantic?.info ?? colors.primary.main, isDark ? 0.55 : 0.4)}`,
              bgcolor: alpha(colors.semantic?.info ?? colors.primary.main, isDark ? 0.16 : 0.08),
            }}
          >
            <Typography variant="body2" sx={{ color: colors.text.primary, flex: '1 1 200px' }}>
              Click a highlighted gap <strong>between lines</strong> where the next scene should
              begin.
            </Typography>
            <Button
              type="button"
              color="inherit"
              size="small"
              variant="text"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => onCancelSplitPlacement?.()}
            >
              Cancel
            </Button>
          </Box>
        )}

        <Box ref={splitOverlayContainerRef} sx={{ position: 'relative' }}>
          {splitPlacementActive &&
            splitGapZones.map(({ splitIndex, top, height }) => (
              <Box
                key={splitIndex}
                role="button"
                tabIndex={0}
                aria-label={`Split before block ${splitIndex + 1}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  runSplitAtLexicalIndex(splitIndex);
                }}
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top,
                  height,
                  zIndex: 10,
                  cursor: 'pointer',
                  bgcolor: alpha(colors.primary.main, isDark ? 0.22 : 0.14),
                  borderTop: `2px dashed ${alpha(colors.primary.main, 0.85)}`,
                  borderBottom: `2px dashed ${alpha(colors.primary.main, 0.85)}`,
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    bgcolor: alpha(colors.primary.main, isDark ? 0.38 : 0.28),
                  },
                }}
              />
            ))}

          {/* Lexical Editor — stable key: remounting on paragraph count wiped unsaved edits */}
          <LexicalComposer key={editorId} initialConfig={initialConfig}>
            <Box sx={{ position: 'relative' }}>
              <EditorContent
                scene={scene}
                sceneLexicalData={sceneLexicalData}
                onChange={scheduleSave}
                onFlushSave={flushPendingSave}
                hasLocalEditsRef={hasLocalEditsRef}
                suppressHydrationRef={suppressHydrationRef}
                editorId={editorId}
                onEditorReady={handleEditorReady}
                hydrationNonce={hydrationNonce}
              />
            </Box>
          </LexicalComposer>
        </Box>
      </Box>
    </>
  );
};

/* eslint-disable react/require-default-props */
EditorBlock.propTypes = {
  scene: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    chapterId: PropTypes.string,
    updatedAt: PropTypes.string,
    data: PropTypes.shape({
      root: PropTypes.shape({
        children: PropTypes.arrayOf(PropTypes.object),
      }),
    }),
    root: PropTypes.shape({
      children: PropTypes.arrayOf(PropTypes.object),
    }),
    children: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
        type: PropTypes.string,
      })
    ),
  }),
  editorIndex: PropTypes.number.isRequired,
  onDragOver: PropTypes.func,
  onDrop: PropTypes.func,
  isDragging: PropTypes.bool,
  onSplitScene: PropTypes.func,
  hydrationNonce: PropTypes.number,
  splitPlacementActive: PropTypes.bool,
  onCancelSplitPlacement: PropTypes.func,
  onSceneWordCountUpdate: PropTypes.func,
  onChapterWordCountUpdate: PropTypes.func,
};
/* eslint-enable react/require-default-props */

EditorBlock.defaultProps = {
  onSplitScene: undefined,
  hydrationNonce: 0,
  splitPlacementActive: false,
  onCancelSplitPlacement: undefined,
  onSceneWordCountUpdate: undefined,
  onChapterWordCountUpdate: undefined,
};

export default EditorBlock;
